import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ViewChild,
} from "@angular/core";

import {
  DataService as CoreDataService,
  PersistMode,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
} from "@barthauer/abc/core/data";

import { DatePipe } from "@angular/common";
import {
  AbcPopupComponent,
  AbcSpeedDialActionComponent,
  AbcTabPanelComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcContextMenuComponent,
  _AbcFileUploaderComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
} from "@barthauer/abc/ui-components";
import {
  AddressPerson,
  DefaultIcons,
  DocumentKind,
  Email,
  GeoDsDocument,
  Person,
  PickListStatus,
} from "@barthauer/custom/xmt-models";
import {
  DxDataGridModule,
  DxFileUploaderModule,
  DxFormModule,
  DxSelectBoxComponent,
  DxValidatorComponent,
} from "devextreme-angular";
import { loadMessages, locale } from "devextreme/localization";
import deMessages from "devextreme/localization/messages/de.json";
import enMessages from "devextreme/localization/messages/en.json";
import { Column, FilterPanelCustomizeTextArg, ToolbarPreparingEvent } from "devextreme/ui/data_grid";
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import { concatMap, finalize, first, switchMap } from "rxjs/operators";
import {
  ActionType,
  NotificationMessage,
  PermissionContext,
  PersonStatus,
  ToastType,
} from "../../models/enumeration-list.model";
import { AppService } from "../../services/app.service";
import { DataService, ObjectKeys } from "../../services/data.service";
import { LanguageService } from "../../services/language.service";
import { NotificationService } from "../../services/notification.service";
import { SharedModule } from "../../shared/shared.module";
import { StaffMembersService } from "./staff-members.service";
import { CheckPermissionDirective } from "../../directives/check-permession-directive";
import { EmailService } from "../../services/email.service";
import { registerUserTemplate } from "../../../../assets/templates/email-templates/register-user-template";
import { ConfigService } from "libs/abc/core/configuration/src/lib/services/config.service";
import { environment } from "../../../../environments/environment";
import { ScreenService } from "../../services/screen.service";
import { CompanyRoleComponent } from "./company-role/company-role.component";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { Item } from "devextreme/ui/context_menu";
import { ProjectPersonViewModel } from "../../models/ProjectPerson.viewModel";
import { NotificationService1, NotificationSettingType } from "../../services/notification1.service";
import { ObjectTypeService } from "../../services/object-type.service";

export class IPerson extends Person {
  AddressPerson?: AddressPerson;
  Position?: string;
  ProjectPerson?: ProjectPersonViewModel
}
@Component({
  selector: "ubt-geods-staff-member",
  templateUrl: "./staff-members.component.html",
  styleUrls: ["./staff-members.component.scss"],
  imports: [
    DxFileUploaderModule,
    DxDataGridModule,
    SharedModule,
    DxFormModule,
    _AbcFormComponent,
    _AbcGridComponent,
    AbcPopupComponent,
    AbcSpeedDialActionComponent,
    _AbcContextMenuComponent,
    _AbcSelectBoxComponent,
    CheckPermissionDirective,
    _AbcButtonComponent,
    AbcTextBoxComponent,
    AbcTabPanelComponent,
    CompanyRoleComponent,
  ],
})
export class StaffMembersComponent implements OnInit, OnDestroy {
  @ViewChild("staffMembersGrid") staffMembersGrid: _AbcGridComponent;
  @ViewChild("fileUpload") staffMembersUploader: _AbcFileUploaderComponent;
  @ViewChild("staffMemberPopup") staffMemberPopup: AbcPopupComponent;
  @ViewChild("confirmDeletePopup") confirmDeletePopup: AbcPopupComponent;
  @ViewChild("contextMenu") contextMenu: _AbcContextMenuComponent;
  @ViewChild("projectRoleSelectBox", { static: true })
  projectRoleSelectBox: DxSelectBoxComponent;
  @ViewChild("form", { static: false }) form: _AbcFormComponent;
  private emailValidator = viewChild<DxValidatorComponent>("emailValidator");

  public data$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  public uploadDocument: GeoDsDocument = new GeoDsDocument(null);
  public staffMember: IPerson = new IPerson(null);
  public staffMemberPosition: AddressPerson = new AddressPerson(null);
  public documents: GeoDsDocument[] = [];
  public staffMembers: IPerson[] = [];
  public uploaded = false;
  public availableRoles: IRole[] = [];
  public availablePosition: IRole[] = [];
  public personColumns: QueryColumn[] = [];
  public functionColumns: QueryColumn[] = [];
  public documentsColumns: QueryColumn[] = [];
  public pickListStatus = PickListStatus;
  public currentStaffMember: IPerson = new IPerson(null);
  private readonly screenService = inject(ScreenService);
  protected readonly isSuperAdmin = this.dataService.isSuperAdmin;

  public columnsHeader: Column[] = [
    {
      caption: this.dataService.res("Ubt-CreateStaffMembers-Picture"),
      dataField: "ProfilePicture",
      dataType: "string",
      visibleIndex: 0,
      hidingPriority: 5,
      allowFiltering: false,
    },
    {
      caption: this.dataService.res("Ubt-CreateStaffMembers-FirstName"),
      dataField: "FirstName",
      dataType: "string",
      visibleIndex: 0,
      hidingPriority: 7,
    },
    {
      caption: this.dataService.res("Ubt-CreateStaffMembers-LastName"),
      dataField: "LastName",
      dataType: "string",
      visibleIndex: 0,
      hidingPriority: 6,
    },
    {
      caption: this.dataService.res("Ubt-CreateStaffMembers-EMail"),
      dataField: "EMail",
      dataType: "string",
      visibleIndex: 0,
      hidingPriority: 4,
    },
    {
      caption: this.dataService.res("Ubt-StaffMembers-Project-Role"),
      dataField: "AddressPerson.Position",
      dataType: "string",
      hidingPriority: 3,
      visible: !this.isSuperAdmin,
      allowHiding: !this.isSuperAdmin,
      showInColumnChooser: !this.isSuperAdmin,
    },
    {
      caption: this.dataService.res("Ubt-CreateStaffMembers-Function"),
      dataField: "Function",
      dataType: "string",
      visibleIndex: 0,
      visible: this.isSuperAdmin,
      hidingPriority: 2,
    },
    {
      caption: this.dataService.res("Ubt-StaffMembers-Project-Status"),
      dataField: "Status",
      visibleIndex: 6,
      allowEditing: false,
      hidingPriority: 1,
    },
    {
      dataField: "Active",
      caption: "Active",
      visible: false,
      allowHiding: false,
      allowEditing: false,
      showInColumnChooser: false,
      allowFiltering: this.isSuperAdmin,
    },
    {
      dataField: "AddressPerson.Status",
      caption:'Active',
      visible:false,
      allowHiding:false,
      allowEditing: false,
      showInColumnChooser: false,
      allowFiltering: !this.isSuperAdmin,
    },
    {
      type: "buttons",
      caption: "",
      alignment: "left",
      dataField: "",
      visibleIndex: 6,
      minWidth: 50,
      width: "50px",
    },
  ];
  public Action = ActionType;
  public PermissionContext = PermissionContext;
  public contextMenuItems: Item[] = [];
  public submitText: string;
  public cancelButtonOptions = {
    text: this.dataService.res("Ubt-CreateMeasure-Cancel"),
    onClick: () => this.clearWindow(),
  };
  private subscriptions: Subscription[] = [];
  private staffMemberId: string;
  public noDataText = this.dataService.res("Ubt-No-Data-text");
  public staffMemeberImage: string = "";
  public readonly exportFileName =
    this.dataService.res("Ubt-TreeList-StaffMembers") +
    "_" +
    this.datePipe.transform(new Date(), "dd.MM.yyyy");

  public canSave: boolean = false;

  protected selectedIndex = 0;
  protected tabItems = [
    {
      index: 0,
      title: this.dataService.res("Ubt-CreateStaffMembers-User-Info"),
    },
    this.isSuperAdmin
      ? {
          index: 1,
          title: `${this.dataService.res("Ubt-CreateStaffMembers-Company")}`,
        }
      : null,
  ].filter((item) => item);
  protected newUserEmail = "";
  protected verifyEmailButtonOptions = {
    text: this.dataService.res("Ubt-CreateStaffMembers-Verify"),
    onClick: () => this.verifyUserExists(),
  };
  protected filters = [
    { name: this.dataService.res("Ubt-Staff-Members-All"), value: 2 },
    { name: this.dataService.res("Ubt-Staff-Members-Active"), value: 1 },
    { name: this.dataService.res("Ubt-Staff-Members-Archived"), value: 0 },
  ];
  protected selectedFilter = signal(1);
  protected archiveFilter = computed(() => {
    switch (this.selectedFilter()) {
      case 0:
        return this.isSuperAdmin 
         ? ['Active', '=', false] 
         : ['AddressPerson.Status', '=',PersonStatus.ARCHIVED];
      case 1:
        return this.isSuperAdmin 
         ? ['Active', '<>', false] 
         : ['AddressPerson.Status', '<>',PersonStatus.ARCHIVED];;
      case 2:
        return null;
    }
  });
  protected customizeFilterPanelText = (e: FilterPanelCustomizeTextArg) => {
    let text: string = e.text;
    const archivedFilter = new RegExp(
      `\\[Active\\]\\s*${this.dataService.res("Ubt-StaffMembers-Equals")}\\s*${this.isSuperAdmin ? `'?${this.dataService.res("Ubt-StaffMembers-False")}'?` : "'[^']*'"}`,
      'gi'
    );
    text = text.replace(archivedFilter, this.dataService.res("Ubt-Staff-Members-Archived")+' '+this.dataService.res("Ubt-StaffMembers-Users"));
    const ActiveFilter = new RegExp(
      `\\[Active\\]\\s*${this.dataService.res("Ubt-StaffMembers-Does-Not-Equal")}\\s*${this.isSuperAdmin ? `'?${this.dataService.res("Ubt-StaffMembers-False")}'?` : "'[^']*'"}`,
      'gi'
    );
    text = text.replace(ActiveFilter, this.dataService.res("Ubt-Staff-Members-Active")+' '+this.dataService.res("Ubt-StaffMembers-Users"));
    
    return text;
  };
  protected isActive = signal(true);
  protected restoreOrDeleteText = computed(() =>
    this.isActive()
      ? this.dataService.res("Ubt-CreateStaffMembers-Delete-Message")
      : this.dataService.res("Ubt-CreateStaffMembers-Restore-Message"),
  );
  protected isPersonExists = true;
  protected isAddressPersonExists = true;
  protected isEditing = false;

  private readonly currentConfigurationUrl: string;
  private skipValidation = new Map<string, boolean>([
    ["FirstName", false],
    ["LastName", false],
    ["EMail", false],
    ["Function", false],
    ["Login", false],
    ["Position", false],
  ]);
  private isUserArchived=false;

  constructor(
    public dataService: DataService,
    public appService: AppService,
    private coreDataService: CoreDataService,
    private datePipe: DatePipe,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private staffMembersService: StaffMembersService,
    private languageService: LanguageService,
    private emailService: EmailService,
    private configService: ConfigService,
    private notificationService1:NotificationService1,
     private objectTypeService: ObjectTypeService,
  ) {
    this.currentConfigurationUrl =
      configService.getConfiguration().WebApi.BaseURL;
  }

  public ngOnInit(): void {
    this.dataService.floatingButtonConfig();
     this.objectTypeService.initializeObjectTypesList();
    this.appService.showLoader(true);
    this.languageService.localStorageChanges$.subscribe((value) => {
      this.updatedTranslation(value);
    });
    const queries = forkJoin([
      this.dataService.loadListDataSource(ObjectKeys.PERSON, "Function"),
      this.staffMembersService.getPersons(),
      this.dataService.loadListDataSource(ObjectKeys.ADDRESSPERSON, "Position"),
    ]);
    queries.pipe(first()).subscribe({
      next: (data: any) => {
        this.availableRoles = data[0].Data;
        this.availablePosition = data[2].Data;
        this.staffMembersService.positions.set(this.availablePosition);
        if (Array.isArray(data[1]) && data[1].length > 0) {
          this.staffMembers = data[1].map((staffMember) => {
            return {
              ...staffMember,
              AddressPerson: staffMember.AddressPerson?.[0],
            };
          });
          const updatedData = this.staffMembers;
          this.data$.next(updatedData);
        } else {
          this.notificationService.callNotification({
            message: NotificationMessage.Empty,
            type: ToastType.INFO,
          });
        }
      },
      error:(error) => {
        this.notificationService.callNotification({
          message: NotificationMessage.Error,
          type: ToastType.ERROR,
        });
        this.appService.showLoader(false);
      },
      complete:() => {
        this.appService.showLoader(false);
        this.coreDataService.executeReadObjectsQuery(this.staffMembersService.getPersonWithImages()).pipe(first()).subscribe(
          (data)=>{        
            if (Array.isArray(data) && data.length > 0) {
              this.staffMembers = data.map((staffMember) => {
                return {
                  ...staffMember,
                  AddressPerson: staffMember.AddressPerson?.[0],
                };
              });
            }
          }
        )
      },
    });
    this.dataService.uploadData$.subscribe((uploadData: GeoDsDocument) => {
      this.staffMemeberImage = uploadData.Object;
      this.uploaded = true;
      this.uploadDocument = uploadData;
      this.updateField("ProfilePicture", uploadData.Object, true);
    });
    this.subscriptions.push(
      this.dataService.updateGridData$.subscribe(() => {
        this.getStaffMemberById(this.staffMemberId);
      }),
    );
  }
  ngAfterViewInit(): void {
    this.screenService.screen$.subscribe(({ screenSize, isMenuOpened }) => {
      this.screenService.setGridColumnsWidth(
        this.columnsHeader,
        this.staffMembersGrid,
        screenSize,
        isMenuOpened,
        this.getColumnWidth,
      );
    });
  }
  private getColumnWidth = (
    key: string,
    isMenuOpened: boolean,
    largeScreenAddition: number = 0,
  ): string => {
    switch (key) {
      case "ProfilePicture":
      case "EMail":
      case "AddressPerson.Position":
      case "Function":
        return isMenuOpened
          ? `${(200 + largeScreenAddition).toString()}px`
          : `${(240 + largeScreenAddition).toString()}px`;

      case "FirstName":
      case "LastName":
        return isMenuOpened
          ? `${(140 + largeScreenAddition).toString()}px`
          : `${(180 + largeScreenAddition).toString()}px`;

      case "Status":
        return isMenuOpened
          ? `${(60 + largeScreenAddition).toString()}px`
          : `${(120 + largeScreenAddition).toString()}px`;
      default:
        return "50px";
    }
  };
  protected onToolbarPreparing(e: ToolbarPreparingEvent): void {
    e.toolbarOptions.items.unshift({
      location: "before",
      template: "archivedTemplate",
    });
  }
  updatedTranslation(value: String) {
    if (value === "de" || value == null) {
      locale("de");
      loadMessages(deMessages);
    } else {
      loadMessages(enMessages);
      locale("en");
    }
  }

  public onCellPrepared = (e) => {
    if (e.rowType === "data" || e.rowType === "detailAdaptive") {
      e.cellElement.style.verticalAlign = "middle";

      if (e.column.dataField === "ProfilePicture") {
        const img = document.createElement("IMG");
        e.cellElement.style.textAlign = "center";

        if (e.data?.Documents) {
          const object = e.data.Documents[0].Object;
          img.setAttribute("src", "data:image/png;base64," + object);
          this.cdr.markForCheck();
        } else {
          e.cellElement.innerHTML = "";
          img.setAttribute("src", "assets/images/Avatar.svg");
          this.cdr.markForCheck();
        }
        img.style.borderRadius = "50%";
        img.style.width = "30%";
        e.cellElement.appendChild(img);
      } else if (e.column.dataField === "") {
        e.cellElement.style.cursor = "pointer";
        return (e.cellElement.innerHTML = DefaultIcons.CONTEXTMENU);
      }
    }
  };

  public onCellClick = (e) => {
    if (e.column?.command === "adaptive") {
      e.event?.stopPropagation();
      return;
    }
    if (e.data) {
      this.staffMemberId = e.data?.Id;
      this.staffMembersService.selectedPersonId.set(this.staffMemberId);
      this.staffMember = e.data;
      this.currentStaffMember = JSON.parse(JSON.stringify(this.staffMember));
      if (e.columnIndex === 6) {
        e.cellElement.className = "context-menu-icon" + e.rowIndex;
        this.contextMenu.target = ".context-menu-icon" + e.rowIndex;
        e.cellElement.innerHTML = this.contextMenu.visible
          ? DefaultIcons.CLOSE
          : DefaultIcons.CONTEXTMENU;
        this.isActive.set((this.isSuperAdmin ? this.staffMember.Active : this.staffMember.AddressPerson.Status != PersonStatus.ARCHIVED) ?? true);
        this.contextMenuItems=[
          {
            text: this.dataService.res("Ubt-StaffMember-Edit-Staff-Member"),
            onClick: () => {
              this.openStaffMemberDialog(true);
            },
            visible:
              (this.isSuperAdmin
                ? true
                : this.dataService.hasPermission(
                    PermissionContext.StaffMembers,
                    ActionType.UPDATE,
                  )) && this.isActive(),
          },
          {
            text: this.isActive()
              ? this.dataService.res("Ubt-StaffMember-Delete-Staff-Member")
              : this.dataService.res("Ubt-StaffMember-Restore-Staff-Member"),
            onClick: () => {
              this.openConfirmDialog();
            },
            visible: this.isSuperAdmin
              ? true
              : this.dataService.hasPermission(
                  PermissionContext.StaffMembers,
                  ActionType.DELETE,
                ),
          },
        ];
        this.contextMenu.instance.show();
      }
    }
  };

  public getDocumentQuery(oPath?: string): QueryObjectsModel {
    this.personColumns = [
      this.coreDataService.createQueryColumn(
        "Id",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "FirstName",
        "FirstName",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "LastName",
        "LastName",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "EMail",
        "EMail",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Login",
        "Login",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Function",
        "Function",
        QueryColumnSortOrder.None,
      ),
    ];
    const documentsColumns = [
      this.coreDataService.createQueryColumn(
        "Id",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Designation",
        "Designation",
        QueryColumnSortOrder.None,
      ),
      {
        Name: "Object",
        OPath: "Object",
        Format: "Base64Image[80x80]",
      },
    ];
    const positionColumns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("Position", "Position"),
    ];
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectKeys.PERSON;
    mainQuery.Columns = this.personColumns;
    mainQuery.OPath =
      "ParentId=" +
      "'" +
      sessionStorage.getItem("profileAddressId") +
      "'" +
      " && Not(LastName = 'Admin')";

    if (oPath) mainQuery.OPath = oPath;
    const subQuery: Query = new Query();
    subQuery.Name = "Documents";
    subQuery.OPath = "Documents";
    subQuery.Columns = documentsColumns;
    const positionQuery: Query = new Query();
    positionQuery.Name = "AddressPerson";
    positionQuery.OPath = "AddressPerson_IdRefPerson_Person";
    positionQuery.Columns = positionColumns;
    mainQuery.ObjectQueries = [subQuery, positionQuery];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  /**
   * Opens the staff member dialog for editing or creating.
   * @param isEditing - True if editing, false if creating.
   */
  public openStaffMemberDialog(isEditing: boolean): void {
    this.staffMemberPosition = new AddressPerson(null);
    this.staffMemberPosition.Position = this.availablePosition[2].Value;
    if (isEditing === true) {
      this.isEditing = true;
      this.isAddressPersonExists = false;
      this.isPersonExists = false;
      this.staffMemberPopup.title = this.dataService.res(
        "Ubt-StaffMembers-Update-StaffMembers",
      );
      this.submitText = "Update";
      this.staffMemeberImage = this.staffMember.Documents?.[0]?.Object
        ? "data:image/png;base64," + this.staffMember.Documents?.[0]?.Object
        : "";
      if (this.staffMember["AddressPerson"]) {
        const addressPerson = new AddressPerson(
          this.staffMember["AddressPerson"],
        );
        this.staffMemberPosition.Id = addressPerson.Id;
        this.staffMemberPosition.Position = addressPerson.Position;
        this.currentStaffMember.Position = addressPerson.Position;
      }
    } else {
      this.uploadDocument = new GeoDsDocument(null);
      this.staffMember = new IPerson(null);
      this.currentStaffMember = new IPerson(null);
      this.staffMemberPosition = new AddressPerson(null);
      this.form.instance.resetValues();
      this.staffMemeberImage = "";
      this.staffMemberPopup.title = this.dataService.res(
        "Ubt-StaffMembers-Create-StaffMembers",
      );
      this.submitText = "Create";
      this.staffMembersService.selectedPersonId.set("");
    }
    this.staffMemberPopup.instance.show();
  }

  /**
   * Updates a field value in the staff member object.
   * check if the form values has changed or the document has been uploaded.
   * check if the email is valid.
   * check if the form is valid.
   * @param field - The field name to update.
   * @param value - The new value for the field.
   * @param isDocument - True if the field is a document, false otherwise.
   */

  protected updateField(
    field: string,
    value: string,
    isDocument: boolean = false,
  ): void {
    if (this.skipValidation.get(field)) {
      this.skipValidation.set(field, false);
      return;
    }
    if (!isDocument) {
      this.staffMember = { ...this.staffMember, [field]: value };
    }
    if (!this.staffMemberPopup.visible) return;

    this.evaluateCanSave();
  }

  protected setArchivedVisibility(e: ValueChangedEvent): void {
    this.selectedFilter.set(e.value);
  }

  private evaluateCanSave(): void {
    const emailRegex = /^[\w._-]+@[\w.-]+\.[a-z]{2,}$/i;
    const isValidEmail = emailRegex.test(this.staffMember.EMail || "");
    const validation = this.form.instance.validate();

    const hasChanged = this.currentStaffMember.Id
      ? this.dataService.compareObjects(
          this.currentStaffMember,
          this.staffMember,
          ["Position", "AddressPerson"],
        )
      : true;
    const positionChanged =
      this.currentStaffMember.Position !== this.staffMemberPosition.Position;
      this.canSave = !this.isUserArchived &&
      validation.isValid &&
      isValidEmail &&
      (hasChanged || this.uploaded || positionChanged);
  }

  /**
   * Persists the staff member data, handling email uniqueness.
   */
  protected persistStaffMember(): void {
    this.appService.showLoader(true);
    this.canSave = false;

    if (!this.staffMember.Id) {
      this.staffMember.Status = PickListStatus.NEW;
    }

    const emailChanged =
      this.currentStaffMember.EMail !== this.staffMember.EMail;

    const saveStaff$ = emailChanged
      ? this.staffMembersService.isEmailUsed(this.staffMember.EMail).pipe(
          switchMap((isUsed) => {
            if (isUsed) {
              this.appService.showLoader(false);
              this.notificationService.callNotification({
                message: this.dataService.res(
                  "Ubt-CreateStaffMembers-EmailIsUsed",
                ),
                type: ToastType.ERROR,
              });
              return throwError(() => new Error("EMAIL_USED"));
            }
            return this.persistStaffMemberData({ ...this.staffMember });
          }),
        )
      : this.persistStaffMemberData({ ...this.staffMember });

    this.subscriptions.push(
      saveStaff$.subscribe({
        next: () => {},
        error: (error) => {
          if (error.message !== "EMAIL_USED") {
            this.appService.showLoader(false);
          }
        },
        complete: () => {
          this.appService.showLoader(false);
          this.clearWindow();
          this.cdr.detectChanges();
        },
      }),
    );
  }

  /**
   * Persists staff member data, handling email uniqueness.
   * @param staffMember - The staff member to persist.
   * @returns Observable of the persisted staff member data.
   */
  private persistStaffMemberData(
    staffMember: IPerson,
  ): Observable<ArrayBuffer[] | void> {
    const projectId= sessionStorage.getItem('projectId');
    return (this.isPersonExists
      ? of(null)
      : this.staffMembersService.persistStaffMember(staffMember).pipe(
          concatMap((data) => {
            this.staffMemberId = data.Id;
            this.staffMember.Id = data.Id;
            const addressId = sessionStorage.getItem("profileAddressId");

            this.staffMemberPosition.IdRefPerson = data.Id;
            this.staffMemberPosition.ParentId = addressId;
            const document = this.getDocument(
              data.Id,
              staffMember.Documents?.[0]?.Id || null,
            );
            return this.createDocument(document).pipe(
               concatMap(() =>
              this.ensureNotificationSettings(data.Id))
            )
          }),
        ))
        .pipe(
          concatMap(()=>this.createPosition(this.staffMemberPosition)),
          concatMap(()=>(staffMember.ProjectPerson || this.isEditing || !projectId) ? of(null) : this.createProjectPerson(this.staffMember.Id,projectId)),
          concatMap(() => of(this.getStaffMemberById(staffMember.Id)))
        )
  }

  /**
   * Sends an email invitation to the staff member.
   */
  protected sendEmailInvitation() {
    this.appService.showLoader(true);
    const toDayDate = new Date().getTime();
    const landingPageUrl = `${this.currentConfigurationUrl}/aims/register/${btoa(this.staffMember.Id + "&&&" + toDayDate)}`;
    const logoUrl = `${this.dataService.currentCongfiguration.WebApi.BaseURL}/aims/assets/images/aims-logo.svg`;

    const emailContent = {
      logoUrl: logoUrl,
      person: {
        FirstName: this.staffMember.FirstName,
        LastName: this.staffMember.LastName,
        username: this.staffMember.Login,
      },
      landingPageUrl: landingPageUrl,
    };
    const emailBody = this.emailService.prepareEmailBody(
      registerUserTemplate,
      emailContent,
    );
    const email: Email = {
      Subject: this.dataService.res("user-emailSubject"),
      Body: emailBody,
      From: environment.Email.systemEmail,
      To: this.staffMember.EMail,
    };
    const updateStatusIfNew = () => {
      if (this.staffMember.Status !== PickListStatus.NEW) {
        return of(null);
      }
     return this.ensureNotificationSettings(this.staffMember.Id).pipe(
    switchMap(() =>
      this.persistStaffMemberData(this.staffMember)
    )
  );
    };

    this.emailService
      .sendEmail(email)
      .pipe(
        switchMap(() => updateStatusIfNew()),
        finalize(() => this.appService.showLoader(false)),
      )
      .subscribe({
        next: () => {
          this.notificationService.callNotification({
            message: this.dataService.res("Ubt-CreateStaffMembers-EmailSent"),
            type: ToastType.SUCCESS,
          });
        },
        error: (error) => {
          this.notificationService.callNotification({
            message: error,
            type: ToastType.ERROR,
          });
        },
      });
  }

  private createDocument = (document): Observable<any> => {
    if (
      this.uploadDocument.Object &&
      this.uploadDocument.FileName?.length > 0
    ) {
      return of(this.dataService.persistUploadDocument(document));
    } else {
      return of([this.getStaffMemberById(this.staffMemberId)]);
    }
  };

  private createPosition = (memberPosition: AddressPerson): Observable<any> => {
    return this.isSuperAdmin
      ? of(null)
      : of(this.persistStafMemeberPosition(memberPosition));
  };
  
  private createProjectPerson(personId:string,projectId:string): Observable<ArrayBuffer>{
    return this.staffMembersService.createProjectPerson(personId,projectId);
  }

  public persistStafMemeberPosition = (memberPosition: AddressPerson): void => {
    this.staffMembersService
      .persistStafMemeberPosition(memberPosition)
      .subscribe(
        (_position) => {
          this.staffMemberPosition.Id = _position.Id;
        },
        (error) => {
          this.notificationService.callNotification({
            message: error,
            type: ToastType.ERROR,
          });
          this.appService.showLoader(false);
        },
        () => {},
      );
  };

  public getDocument(parentId: string, documentId?: string): GeoDsDocument {
    this.uploadDocument.Id = documentId;
    this.uploadDocument.ParentId = parentId;
    this.uploadDocument.Designation = "imgStaffMember";
    this.uploadDocument.ParentObjectType = ObjectKeys.PERSON;
    this.uploadDocument.Kind = DocumentKind.Bild;
    return this.uploadDocument;
  }

  private getStaffMemberById(id: string): void {
    const opath = "Id=" + "'" + id + "'";
    this.subscriptions.push(
      this.coreDataService
        .executeReadObjectsQuery(
          this.staffMembersService.getPersonWithImages(opath),
        )
        .subscribe(
          (data) => {
            if (this.staffMembersService.isUpdating === true) {
              let selectedIndex: number;
              this.staffMembers.forEach((item, index) => {
                if (item.Id.toString() == id) {
                  selectedIndex = index;
                }
              });
              this.staffMembers[selectedIndex] = {
                ...data[0],
                AddressPerson: data[0].AddressPerson?.[0],
              };
              this.staffMembersGrid.instance.refresh();
            } else {
              this.staffMembers = [
                ...this.staffMembers,
                { ...data[0], AddressPerson: data[0].AddressPerson?.[0] },
              ];
            }
            this.notificationService.callNotification({
              message: NotificationMessage.ValidUpdateMessage,
              type: ToastType.SUCCESS,
            });
          },
          (error) => {
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
          },
        ),
    );
  }

  public clearWindow(): void {
    this.staffMemberPopup.instance.hide();
    this.uploadDocument = new GeoDsDocument(null);
    this.appService.showLoader(false);
    this.staffMember = new Person(null);
    this.currentStaffMember = new Person(null);
    this.canSave = false;
    this.staffMemeberImage = "";
    this.uploaded = false;
    this.staffMemberPosition = new AddressPerson(null);
    this.newUserEmail = "";
    this.isPersonExists = true;
    this.isAddressPersonExists = true;
    this.isEditing = false;
    this.staffMembersUploader.instance.reset();
    this.emailValidator()?.instance.reset();
    this.staffMembersService.trackPopup.set(
      !this.staffMembersService.trackPopup(),
    );
    this.isUserArchived=false;
    this.cdr.detectChanges();
  }

  public onFileUpload(e, document): void {
    this.dataService.onFileChanged(e, document);
  }

  protected archiveOrRestoreStaffMember(): void {
    if (this.isSuperAdmin) this.archiveOrRestorePerson();
    else this.archiveOrRestoreAddressPerson();
  }
  private archiveOrRestorePerson(): void {
    this.staffMembersService
      .archiveOrRestorePerson(this.staffMemberId, this.staffMember.Active)
      .subscribe({
        complete: () => {
          this.confirmDeletePopup.instance.hide();
          this.staffMembers = this.staffMembers.map((member) =>
            member.Id === this.staffMemberId
              ? { ...member, Active: !member.Active }
              : member,
          );
          this.notificationService.callNotification({
            message: NotificationMessage.ValidUpdateMessage,
            type: ToastType.SUCCESS,
          });
        },
        error: () => {
          this.notificationService.callNotification({
            message: NotificationMessage.Error,
            type: ToastType.ERROR,
          });
        },
      });
  }
  private archiveOrRestoreAddressPerson():void{
    this.staffMembersService.archiveOrRestoreAddressPerson(this.staffMember.AddressPerson.Id,this.staffMember.AddressPerson.Status).subscribe({
      complete:()=>{
        this.confirmDeletePopup.instance.hide();
        this.staffMembers = this.staffMembers.map(member =>
          member.Id === this.staffMemberId 
           ? {
                ...member,
                AddressPerson:{
                  ...member.AddressPerson,
                  Status:this.staffMembersService.reverseStatus(member.AddressPerson.Status)
                }
              } 
           : member);
        this.notificationService.callNotification({
          message: NotificationMessage.ValidUpdateMessage,
          type: ToastType.SUCCESS,
        });
      },
      error: () => {
        this.notificationService.callNotification({
          message: NotificationMessage.Error,
          type: ToastType.ERROR,
        });
      }
    });
  }

  public openConfirmDialog(): void {
    this.confirmDeletePopup.instance.show();
  }

  public abortDelete(): void {
    this.confirmDeletePopup.instance.hide();
  }
  private verifyUserExists(): void {
    this.isPersonExists = true;
    this.isAddressPersonExists = true;
    this.canSave = false;
    this.isUserArchived=false;
    if (this.emailValidator().instance.validate().isValid) {
      this.setSkipValidation(true, true);
      this.staffMember = new IPerson(null);
      this.staffMemberPosition = new AddressPerson(null);
      this.staffMembersService
        .verifyUserExists(this.newUserEmail)
        .pipe(first())
        .subscribe((person) => {
          if (Array.isArray(person) && person.length > 0) {
            this.setSkipValidation(true);
            this.staffMember = person[0];
            this.staffMemberPosition.ParentId =
              sessionStorage.getItem("profileAddressId");
            this.staffMemberPosition.IdRefPerson = this.staffMember.Id;
            this.currentStaffMember.EMail = this.staffMember.EMail;
            this.staffMembersService.selectedPersonId.set(this.staffMember.Id);
            if (person[0].hasOwnProperty(ObjectKeys.ADDRESSPERSON)) {
              this.staffMember.ProjectPerson ? this.setSkipValidation(false, true, true) : this.setSkipValidation(false, true, false);
              this.staffMemberPosition = {...this.staffMemberPosition, ...this.staffMember.AddressPerson[0]};
            } else {
              this.submitText = "Associate";
              this.isAddressPersonExists = false;
            }
            if(!this.staffMember.Active){
              this.isUserArchived=true;
              this.notificationService.callNotification({
                message: this.dataService.res("Ubt-StaffMembers-User-Archived"),
                type: ToastType.ERROR,
              });
            }
          } else {
            this.submitText = "Create";
            this.staffMember = {
              ...this.staffMember,
              EMail: this.newUserEmail,
            };
            this.setSkipValidation(false);
            this.currentStaffMember.EMail = this.newUserEmail;
            this.isPersonExists = false;
          }
        });
    }
  }
  private setSkipValidation(
    isPerson: boolean,
    isAddressPerson: boolean = false,
    isEmail: boolean = true,
  ): void {
    if (isPerson)
      this.skipValidation.forEach((_value, _key) => {
        this.skipValidation.set(_key, true);
      });
    this.skipValidation.set("Position", isAddressPerson);
    this.skipValidation.set("EMail", isEmail);
  }
  private ensureNotificationSettings(personId: string): Observable<any> {
  return this.notificationService1
    .getNotificationSettings(personId, [
         NotificationSettingType.EMAIL,
    ])
    .pipe(
      switchMap((properties) => {

        const exists = properties.some(
          (property) =>
            property.Designation ===
               NotificationSettingType.EMAIL,
        );

        if (exists) {
          return of(null);
        }

        return this.notificationService1.setNotificationSettings(
          PersistMode.Insert,
            NotificationSettingType.EMAIL,
          false,
          '',
          null,
          personId,
        );
      }),
    );
}
  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => {
      subscription.unsubscribe();
    });
  }
}

interface IRole {
  Description: string;
  Value: string;
}
